import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAuthToken } from "../../../utils/getAuthToken";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Async thunk to stop a workflow run (delete enrollment)
export const stopWorkflowRun = createAsyncThunk(
  'workflowRuns/stopRun',
  async ({ workflowId, enrollmentId }, { rejectWithValue }) => {
    try {
      // Get token from memory (not localStorage)
      const token = await getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Call the API to stop the workflow run
      const response = await axios.delete(
        `${SERVER_DOMAIN}/api/workflow/stop-run/${workflowId}/${enrollmentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to get workflow runs with pagination
export const getWorkflowRuns = createAsyncThunk(
  'workflowRuns/getRuns',
  async ({ workflowId, startTime, endTime, page = 1, limit = 10, search = "" }, { rejectWithValue }) => {
    try {
      // Get token from memory (not localStorage)
      const token = await getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const requestBody = {
        startTime,
        endTime,
        page,
        limit,
        search
      };
      
      console.log('Sending request to server:', requestBody);
      
      const response = await axios.post(
        `${SERVER_DOMAIN}/api/workflow/getruns/${workflowId}`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  runs: [],
  pagination: {
    currentPage: 1,
    totalPages: 0,
    totalRuns: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  },
  loading: false,
  error: null,
  stopLoading: false,
  stopError: null
};

const workflowRunsSlice = createSlice({
  name: 'workflowRuns',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.stopError = null;
    },
    clearRuns: (state) => {
      state.runs = [];
      state.pagination = initialState.pagination;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get workflow runs
      .addCase(getWorkflowRuns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getWorkflowRuns.fulfilled, (state, action) => {
        state.loading = false;
        state.runs = action.payload.runs || [];
        state.pagination = action.payload.pagination || initialState.pagination;
        state.error = null;
      })
      .addCase(getWorkflowRuns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Stop workflow run
      .addCase(stopWorkflowRun.pending, (state) => {
        state.stopLoading = true;
        state.stopError = null;
      })
      .addCase(stopWorkflowRun.fulfilled, (state, action) => {
        state.stopLoading = false;
        // Remove the stopped run from the list
        state.runs = state.runs.filter(run => run._id !== action.payload.enrollmentId);
        // Update total count
        state.pagination.totalRuns = Math.max(0, state.pagination.totalRuns - 1);
        state.stopError = null;
      })
      .addCase(stopWorkflowRun.rejected, (state, action) => {
        state.stopLoading = false;
        state.stopError = action.payload;
      });
  }
});

export const { clearError, clearRuns } = workflowRunsSlice.actions;
export default workflowRunsSlice.reducer;
